import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { setAuthTokens, clearAuthTokens } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // skip /me check if landing from OAuth callback so AuthCallback can do the exchange first
    if (window.location.hash?.includes("session_id=")) {
      setUser(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Mirror tokens to memory + localStorage → Bearer fallback for browsers
    // that block cookies (mobile privacy modes / in-app webviews).
    setAuthTokens(data || {});
    // Enter the app straight from the login response — no dependency on a
    // second request succeeding. The response includes permissions, so the
    // UI has everything it needs even if cookies AND storage are blocked.
    if (data?.user) {
      setUser(data.user);
      // Background sanity check — only overwrite on success so a transient
      // mobile network hiccup can't bounce the user back to the login page.
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
      return data.user;
    }
    const u = await refresh();
    if (!u) {
      throw new Error("Signed in, but your browser blocked the session. Please try again — if it repeats, open the app in Chrome/Safari directly (not inside another app's browser).");
    }
    return u;
  }, [refresh]);

  const googleSession = useCallback(async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    setAuthTokens(data || {});
    if (data?.user) {
      setUser(data.user);
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
      return data.user;
    }
    return await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed (continuing client-side):", err);
    }
    clearAuthTokens();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, logout, refresh, googleSession }),
    [user, login, logout, refresh, googleSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
